// src/theme.ts — NYMR Info Hub heritage-railway design tokens.
// Verbatim port of docs/prototype/theme.jsx. Do not edit without re-syncing.
//
// Slice J: five tokens are now CSS custom properties so the user-chosen
// theme (warm-gold default; nymr-website alternative) can override them
// at runtime. The var() fallbacks match the warm-gold values so the app
// renders correctly before useTheme has mounted. See src/lib/themes.ts
// for the source-of-truth THEMES constant and src/hooks/useTheme.ts for
// the hook that publishes the vars on document.documentElement.

export const C = {
  // surfaces  -- every surface / text / hairline token below is a CSS custom
  // property with the warm-gold value as its fallback, so a skin (including
  // the Dark Mode skin) can re-colour the whole shell at runtime. See
  // src/lib/themes.ts for the token table and useTheme for the publisher.
  paper:       "var(--c-bg, #FBF6E8)",
  paperWarm:   "var(--c-paper-warm, #F5EBD3)",
  paperSoft:   "var(--c-paper-soft, #FFFDF6)",
  card:        "var(--c-card, #FFFFFF)",

  // text
  ink:         "var(--c-ink, #2B2014)",
  inkSoft:     "var(--c-ink-soft, #604C32)",
  inkMute:     "var(--c-ink-mute, #9F8B6B)",
  inkOnDark:   "#FBF4DF",
  inkOnDarkS:  "rgba(251,244,223,0.72)",

  // brass / amber primary  -- C.brass maps to the user's `accent` token;
  // C.brassDk maps to `link`. The other brass variants stay hardcoded.
  brass:       "var(--c-accent, #A6771C)",
  brassDk:     "var(--c-link, #7A540B)",
  brassLt:     "var(--c-accent-lt, #D9B466)",
  brassGlow:   "var(--c-accent-glow, #F4E2A8)",
  brassBg:     "var(--c-accent-bg, #FBEFCB)",
  brassSurf:   "var(--c-accent-surf, #FFF6DC)",

  // heather secondary
  heather:     "#7A5D8C",
  heatherDk:   "var(--c-heather-dk, #56406A)",
  heatherLt:   "#C6B0D2",
  heatherBg:   "var(--c-heather-bg, #EBE0F0)",

  // sage / moss tertiary
  sage:        "#6E8C72",
  sageDk:      "var(--c-sage-dk, #4F6852)",
  sageLt:      "#BCD0BD",
  sageBg:      "var(--c-sage-bg, #E4ECDF)",

  // signal red — alerts only
  signal:      "#B83A2B",
  signalDk:    "var(--c-signal-dk, #8A2A1F)",
  signalBg:    "var(--c-signal-bg, #F8D9D2)",

  // hairlines
  hairline:    "var(--c-hairline, #E8DCBC)",
  hairlineLt:  "var(--c-hairline-lt, #F0E7D0)",

  // shadows
  shadowS: "var(--c-shadow-s, 0 1px 0 rgba(122,84,11,0.04), 0 2px 6px rgba(122,84,11,0.06))",
  shadowM: "var(--c-shadow-m, 0 1px 0 rgba(122,84,11,0.04), 0 8px 22px rgba(122,84,11,0.10))",
  shadowL: "var(--c-shadow-l, 0 2px 0 rgba(122,84,11,0.05), 0 22px 50px rgba(122,84,11,0.16))",
} as const;

export const F = {
  serif:  "'Playfair Display','Times New Roman',serif",
  body:   "'Nunito','Segoe UI',sans-serif",
  mono:   "'IBM Plex Mono','Courier New',monospace",
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Events Live bridge lane (EL) — the deep racing-green + gold lane that links
// InfoHub to the Events Manager. Distinct from the existing lanes (sage=duty,
// brass=safety, heather=secondary, signal=alert). Used by the Home crew-call /
// Events-Live banners, the Crew Call page, and the dark Live Duty View island.
// Verbatim from the approved prototype (events-live/tokens.jsx EL + the Events
// .day-view dark-island values). Kept as its own export (like IM) so the rest of
// the Hub theme is untouched.
// ─────────────────────────────────────────────────────────────────────────────
export const EL = {
  // bridge lane (racing green + gold)
  base:        "#0a4a3a",
  deep:        "#073629",
  gold:        "#c9a13a",
  goldGlow:    "#f0d98a",
  bg:          "#E2ECE6",
  surf:        "#EFF4EF",
  line:        "#BCD0C4",
  ink:         "#0a4a3a",
  // calmer "availability sent" sage card (pending / approved states)
  sage:        "#6E8C72",
  sageDk:      "#4F6852",
  sageBg:      "#E4ECDF",
  // dark "Live Day View" island — ported from Events src/index.css (.day-view-*)
  darkBg:      "#1a1a1a",
  darkInk:     "#fdfbf6",
  darkInkSoft: "rgba(253,251,246,0.72)",
  darkTop:     "linear-gradient(180deg,#14322a 0%,#0a4a3a 100%)",
  darkCard:    "rgba(245,241,234,0.04)",
  darkLine:    "rgba(245,241,234,0.12)",
  liveRed:     "#ef5350",
  connGreen:   "#80e8a8",
  brass:       "#9b7f2a",
  brassBright: "#c9a13a",
} as const;

// Layout caps (carry over from previous theme.ts — not in prototype but useful)
export const layout = {
  maxContentPx: 720,
  bottomNavH: 64,
  topBarH: 56,
} as const;

// Legacy compatibility shim — the old `theme` export is kept so existing
// code (ContactsPage, ContactModal, App) compiles unchanged until commit 4
// rewrites those files. Remove the shim once nothing references it.
export const theme = {
  paper: C.paper,
  card: C.card,
  inset: C.paperWarm,
  ink: C.ink,
  muted: C.inkSoft,
  hairline: C.hairline,
  brass: C.brass,
  brassDeep: C.brassDk,
  heather: C.heather,
  sage: C.sage,
  signal: C.signal,
  fontDisplay: F.serif,
  fontBody: F.body,
  fontMono: F.mono,
  radiusSm: "6px",
  radiusMd: "12px",
  radiusLg: "20px",
  shadowSm: C.shadowS,
  shadowMd: C.shadowM,
  maxContentPx: layout.maxContentPx,
  bottomNavH: layout.bottomNavH + "px",
  topBarH: layout.topBarH + "px",
} as const;

export type Theme = typeof theme;

// ─────────────────────────────────────────────────────────────────────────────
// Issue Manager design tokens (CODE-001)
//
// Verbatim port of docs/build-plan/issue-manager-build/issue-manager-build/
// src/theme.jsx. The Issue Manager module uses a distinct visual language from
// the rest of the Hub: royal blue (NYMR.co.uk primary), Newsreader serif,
// heritage cream and ink. Kept as a separate `IM` / `IF` export rather than
// folded into `C` / `F` so the existing Hub theme is unaffected, and so
// "match prototype pixel-for-pixel" deviations stay localised to one place.
// See docs/.../docs/DESIGN-TOKENS.md for the authoring intent.
// ─────────────────────────────────────────────────────────────────────────────

export const IM = {
  // surfaces
  paper:      "#f7f4ec",
  paperWarm:  "#efe9d8",
  panel:      "#ffffff",
  panelAlt:   "#f8fafc",
  shell:      "#eef1f6",

  // text / ink
  ink:        "#0f1924",
  inkSoft:    "#3b4a5c",
  muted:      "#6b7a8e",

  // hairlines / rules
  hair:       "#dde3ec",
  rule:       "#e8ecf2",

  // brand — royal (NYMR.co.uk primary)
  royal:      "#1e3a8a",
  royalDeep:  "#142a66",
  royalSoft:  "#e8edf9",
  royalLine:  "#c8d3ed",

  // editorial accents
  brass:      "#b08440",
  brassSoft:  "#f3ead7",
  heather:    "#7d5a8c",
  moss:       "#5a6b3d",

  // status colours
  open:       "#1e3a8a", // royal
  progress:   "#b08440", // brass
  waiting:    "#7d5a8c", // heather
  approval:   "#476681",
  closed:     "#5a6b3d", // moss
  paused:     "#6b7a8e",
  alert:      "#b13a2e",

  // priority colours
  pNormal:    "#6b7a8e",
  pLow:       "#9aa6b5",
  pMed:       "#b08440",
  pHigh:      "#b13a2e",
  pUrgent:    "#7a1d14",

  // SLA bands
  slaGreen:   "#5a6b3d",
  slaAmber:   "#b08440",
  slaRed:     "#b13a2e",
  slaExcluded: "#9aa6b5", // muted grey — issue is outside SLA tracking

  // radii
  r1: 6,
  r2: 10,
  r3: 14,
  r4: 20,
  r5: 28,

  // shadows
  shadowCard: "0 1px 0 rgba(15,25,36,0.03), 0 1px 2px rgba(15,25,36,0.06)",
  shadowPop:  "0 12px 32px rgba(15,25,36,0.18), 0 2px 8px rgba(15,25,36,0.08)",
  shadowWin:  "0 30px 80px rgba(15,25,36,0.22), 0 4px 16px rgba(15,25,36,0.1)",
} as const;

export const IF = {
  display: "'Newsreader','Source Serif Pro',Georgia,serif",
  ui:      "'Inter',-apple-system,BlinkMacSystemFont,system-ui,sans-serif",
  mono:    "'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,monospace",
} as const;

export type IssueStatusKey = "open" | "progress" | "waiting" | "approval" | "closed" | "paused";
export type IssuePriorityKey = "pNormal" | "pLow" | "pMed" | "pHigh" | "pUrgent";
export type SlaState = "on-track" | "amber" | "red" | "excluded";